<template>
  <div>
    <main-layout
      controlsBg="light"
      customControls
      :breadcrumb="getBreadcrumb"
      :showPoweredBy="false"
      :has-settings="true">
      <explorer-topbar-settings
        slot="settings"
        v-on:onClickDownload="onClickDownload"
        v-on:onClickCopy="showCopyModal"
        v-on:onClickSave="showSaveModal"/>
      <div slot="custom-controls-top">
        <div class="controls__filters note">
          <controls-filter
            dateId="startDate"
            :filterTitle="$t('explorer.from')"
            formatDate="DD MMMM YYYY / HH:mm:ss"
            :disabledDate="disabledDate"
            :startDate="getStartDateString">
          </controls-filter>
          <controls-filter
            dateId="endDate"
            :filterTitle="$t('explorer.to')"
            formatDate="DD MMMM YYYY / HH:mm:ss"
            :disabledDate="disabledDate"
            :startDate="getEndDateString">
          </controls-filter>
        </div>
        <btn-pill
          class="update-button"
          :btnText="$t('explorer.menu.explore')"
          btn-size="xs"
          v-on:click="requestData">
        </btn-pill>
      </div>
      <div class="container">
        <div class="header">
          <div class="header__title">
            <h1
              class="flex">
              <chevron
                @click="back()"/>
              {{$t('explorer.menu.evolution.label')}}
              <div
                class="flex"
                v-if="getSampledVariables().length > 0"
                v-tooltip.right.top-start="{ content: '<b>' + $t('explorer.sampledVariables') + '</b></br>' + getSampledVariablesTooltipContent }">
                <font-awesome-icon
                  class="icon"
                  :icon="icon.exclamation"/>
              </div>
            </h1>
          </div>
          <options-evolution
            ref="optionsEvolution"
            :data="getTimelineData"
            v-on:onFixedAxes="onFixedAxes"
            v-on:onSingleAxis="onSingleAxis"
            v-on:onCorrelation="onCorrelation"
            v-on:onGridStateChange="onGridStateChange"
            v-on:onShowPointsChange="onShowPointsChange"/>
        </div>
        <chart-evolution
          :data="getTimelineData"
          :range="getRange"
          :minDate="getMindate()"
          :singleAxis="singleAxis"
          :grid="grid"
          :points="showPoints"
          v-on:zoom="onZoom">
        </chart-evolution>
      </div>
      <copy-url-modal
        ref="copyUrlModal"/>
      <query-save-modal
        ref="querySaveModal"
        v-on:save="createExplorerQuery"/>
      <fixed-range-modal
        ref="fixedRangeModal"
        :data="getTimelineData"
        v-on:change="onFixedAxesRangeChanged"/>
    </main-layout>
  </div>
</template>

<script>
import {eventBus} from '@/main';
import config from 'BRAND/config';
import dayjs from 'dayjs';
import ControlsFilter from '@/components/shared/controlsbar/ControlsFilter';
import { mapActions, mapGetters } from 'vuex';
import MainLayout from '../MainLayout';
import ChartEvolution from '@/components/explorer/evolution/ChartEvolution';
import BtnPill from '@/components/shared/buttons/BtnPill';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faExclamationCircle} from '@fortawesome/pro-regular-svg-icons';
import OptionsEvolution from '@/components/explorer/evolution/OptionsEvolution';
import CopyUrlModal from '@/components/explorer/CopyUrlModal';
import QuerySaveModal from '@/components/explorer/QuerySaveModal';
import FixedRangeModal from '@/components/explorer/evolution/FixedRangeModal';
import ExplorerTopbarSettings from '@/components/explorer/ExplorerTopbarSettings';
import explorerToCSV from '@/layouts/explorer/explorerToCSV';
import Chevron from '@/components/shared/Chevron';
import navigation from '@/layouts/explorer/navigation';

export default {
  components: {
    MainLayout,
    ControlsFilter,
    ChartEvolution,
    BtnPill,
    FontAwesomeIcon,
    OptionsEvolution,
    CopyUrlModal,
    QuerySaveModal,
    FixedRangeModal,
    ExplorerTopbarSettings,
    Chevron
  },
  data () {
    return {
      variableIds: '',
      startDate: getInitialStartDate(),
      endDate: getInitialEndDate(),
      range: [],
      icon: {
        exclamation: faExclamationCircle
      },
      singleAxis: true,
      grid: false,
      showPoints: false,
      origin: null,
      filter: null,
      tags: null
    };
  },
  created () {
    const query = this.$route.query;

    const variableIds = query.variables;
    if (variableIds) {
      this.variableIds = variableIds;
      if (query.startDate && query.endDate) {
        this.startDate = new Date(Number(query.startDate));
        this.endDate = new Date(Number(query.endDate));
      }
      if (query.origin) {
        this.origin = query.origin;
      }
      if (query.filter) {
        this.filter = query.filter;
      }
      if (query.tags) {
        this.filterTags = query.tags;
      }
      if (query.optSingleAxis && query.optSingleAxis === 'false' && variableIds.split(',').length === 2) {
        this.singleAxis = false;
      }
      if (query.optGrid && query.optGrid === 'true') {
        this.grid = true;
      }
      if (query.optShowPoints && query.optShowPoints === 'true') {
        this.showPoints = true;
      }
      if (query.optRange) {
        this.range = query.optRange;
      }
      this.requestData();
    } else {
      this.$router.push('/explorer-evolution-variables');
    }

    eventBus.$on('dateSelected', this.onDateSelected);
  },
  mounted () {
    this.$refs.fixedRangeModal.setRange(this.range);
    this.$refs.optionsEvolution.setSingleAxis(this.singleAxis);
    this.$refs.optionsEvolution.setGridVisible(this.grid);
    this.$refs.optionsEvolution.setShowPoints(this.showPoints);
  },
  destroyed () {
    eventBus.$off('dateSelected', this.onDateSelected);
  },
  methods: {
    ...mapActions([
      'retrieveTimelineData',
      'createQuery'
    ]),
    back () {
      switch (this.origin) {
        case 'InsertecCurrentStatus':
          this.$router.push({name: 'EventList', query: {origin: this.origin, tags: this.filterTags.join()}});
          break;
        case 'QueryList':
          const query = this.filter ? {filter: navigation.FILTER_EXPLORER_EVOLUTION} : null;
          this.$router.push({name: 'QueryList', query: query});
          break;
        default:
          this.$router.push({name: 'ExplorerEvolutionVariables', query: this.$route.query});
      }
    },
    disabledDate (date) {
      return date > new Date();
    },
    onDateSelected (event) {
      if (event.id === 'startDate') {
        this.startDate = event.date;
      }
      if (event.id === 'endDate') {
        this.endDate = event.date;
      }
      this.refreshRouter();
    },
    refreshRouter () {
      const query = {
        variables: this.variableIds,
        startDate: this.startDate.getTime(),
        endDate: this.endDate.getTime(),
        tags: this.filterTags
      };
      if (this.origin) {
        query.origin = this.origin;
      }
      if (this.filter) {
        query.filter = this.filter;
      }
      this.$router.push({query: query});
    },
    requestData () {
      const payload = {
        context: this,
        variableIds: this.variableIds,
        startDate: this.startDate < this.endDate ? this.startDate : this.endDate,
        endDate: this.startDate < this.endDate ? this.endDate : this.startDate
      };
      this.retrieveTimelineData(payload);
    },
    onZoom (zoom) {
      this.startDate = new Date(zoom[0]);
      this.endDate = new Date(zoom[1]);
      this.startDate.setSeconds(0);
      this.endDate.setSeconds(0);
      this.refreshRouter();
      this.requestData();
    },
    createExplorerQuery (options) {
      const queryName = options.name;
      const queryWithDates = options.dates;

      let relativePath = this.$route.fullPath;
      relativePath = relativePath.replace('&filter=' + this.filter, '');
      relativePath = relativePath.replace('&origin=' + this.origin, '');
      if (!queryWithDates) {
        // Remove dates from path
        const queryPath = this.$route.query;
        relativePath = relativePath.replace('&startDate=' + queryPath.startDate, '');
        relativePath = relativePath.replace('&endDate=' + queryPath.endDate, '');
      }

      const query = {
        name: queryName,
        relativePath: relativePath,
        star: false
      };
      this.createQuery({ context: this, query: query });
    },
    showCopyModal () {
      this.$refs.copyUrlModal.show();
    },
    showSaveModal () {
      this.$refs.querySaveModal.show();
    },
    getMindate () {
      return config.minDate;
    },
    getSampledVariables () {
      return this.getTimelineData.variables.filter(variable => variable.sampled);
    },
    onFixedAxes () {
      this.$refs.fixedRangeModal.show();
    },
    onFixedAxesRangeChanged (range) {
      this.range = range;
      this.updateUrl();
    },
    onSingleAxis (singleAxis) {
      this.singleAxis = singleAxis;
      this.updateUrl();
    },
    onGridStateChange (gridStatus) {
      this.grid = gridStatus;
      this.updateUrl();
    },
    onShowPointsChange (showPointsStatus) {
      this.showPoints = showPointsStatus;
      this.updateUrl();
    },
    onCorrelation () {
      const variables = this.variableIds.split(',');
      const query = {
        startDate: this.startDate.getTime(),
        endDate: this.endDate.getTime(),
        pair1: variables[0] + ',' + variables[1]
      };
      if (variables.length === 3) {
        query.intensity = variables[2];
      }
      this.$router.push({name: 'ExplorerCorrelation', query: query});
    },
    updateUrl () {
      const newQuery = {};
      const oldQuery = this.$route.query;
      for (let i in oldQuery) {
        if (oldQuery.hasOwnProperty(i)) {
          newQuery[i] = oldQuery[i];
        }
      }
      newQuery.optRange = this.range;
      newQuery.optSingleAxis = String(this.singleAxis);
      newQuery.optGrid = String(this.grid);
      newQuery.optShowPoints = String(this.showPoints);
      this.$router.push({ query: newQuery });
    },
    onClickDownload () {
      explorerToCSV.downloadEvolution(this.getTimelineData.variables, this.getLocale);
    }
  },
  computed: {
    ...mapGetters([
      'getLocale',
      'getTimelineData'
    ]),
    getStartDateString () {
      return this.startDate.toString();
    },
    getEndDateString () {
      return this.endDate.toString();
    },
    getRange () {
      return this.range;
    },
    getBreadcrumb () {
      if (this.filter) {
        return navigation.filteredBreadcrumb(this);
      } else {
        switch (this.origin) {
          case 'InsertecCurrentStatus':
            return [
              {name: this.$t('currentStatus.status'), link: '/current-status'},
              {name: this.$t('events.title'), link: '/event/list?' + 'origin=' + this.origin + '&tags=' + this.filterTags.join()},
              {name: this.$t('explorer.menu.evolution.label'), disabled: true}
            ];
          case 'QueryList':
            return [
              {link: '/workspace', nameKey: 'menu.workspace'},
              {link: '/query/list', nameKey: 'query.title'},
              {nameKey: 'explorer.menu.evolution.label', disabled: true}
            ].map(route => {
              route.name = this.$t(route.nameKey);
              return route;
            });
          default:
            const relativePath = this.$route.fullPath.split('?')[1];
            return [
              {name: this.$t('explorer.breadcrumbParent'), link: '/explore'},
              {name: this.$t('explorer.parameterSelection'), link: '/explorer-evolution-variables?' + relativePath}, // Dirty hack to emulate 'back' button
              {name: this.$t('explorer.menu.evolution.label'), disabled: true}
            ];
        }
      }
    },
    getSampledVariablesTooltipContent () {
      let variables = [];
      for (let i = 0; i < this.getSampledVariables().length; i++) {
        variables.push(this.getSampledVariables()[i].metadata.label);
      }
      return variables.join(',</br>');
    }
  }
};

const getInitialStartDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapBegin;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};

const getInitialEndDate = () => {
  const startOf = config.explorerDefaultDate.startOf;
  const gap = config.explorerDefaultDate.gapEnd;
  const unit = config.explorerDefaultDate.unit;
  return dayjs().startOf(startOf).subtract(gap, unit).toDate();
};
</script>

<style lang="scss" scoped>
  @import "src/stylesheets/variables";

  .container {
    padding-top: $sp-margin;
    padding-bottom: $sp-margin;
    flex-grow: 1;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .form-check {
    display: inline;
  }

  .header {
    display: flex;
    justify-content: space-between;

    &__title {
      margin-bottom: $sp-margin;
      @media (min-width: $breakpoint) {
        display: flex;
        h1 {
          margin-bottom: 0;
          margin-right: $sp-margin;
          padding-right: $sp-margin;
        }
      }
    }
  }

  .chevron {
    font-size: $fs-2xl;
    color: $jamaicol;
    margin-left: auto;
    margin-right: $sp-margin-smaller;
    cursor: pointer;
  }

  .opacity:hover {
    opacity: 0.3;
    transition: opacity 0.3s;
  }

  .update-button {
    margin-left: $sp;
  }

  .flex {
    display: flex;
    align-items: center;
  }

  .icon {
    font-size: $fs;
    color: $jamaicol;
    margin-left: $sp-margin;
  }

</style>



// WEBPACK FOOTER //
// src/layouts/explorer/ExplorerEvolution.vue